import React, { useEffect, useState } from 'react';
import { CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';

import { Button } from '@/Components/ui/button';

import { linkedInCallback } from './broadcastApi';

/**
 * /hirewithus/linkedin/callback — where LinkedIn returns the user after they
 * approve (or decline) connecting their account for job posts.
 *
 * It normally runs inside the small window the Broadcast panel opened: it
 * hands the one-time code to the API (with the signed-in user's own auth, so
 * the connection is attached to the person actually sitting here), tells the
 * opener the result and closes itself. If there is no opener (the popup was
 * blocked and the whole tab went to LinkedIn) it returns to the page the
 * user came from.
 */

export const LINKEDIN_MESSAGE = 'sj-linkedin-connect';
export const LINKEDIN_RETURN_KEY = 'sj_linkedin_return';

// An authorisation code can be redeemed once. React's dev double-mount would
// otherwise send it twice and turn a success into an error.
const redeemed = new Map();

function redeem(code, state) {
  if (!redeemed.has(code)) redeemed.set(code, linkedInCallback({ code, state }));
  return redeemed.get(code);
}

const DECLINED = new Set(['user_cancelled_login', 'user_cancelled_authorize', 'access_denied']);

export default function LinkedInCallback() {
  const [status, setStatus] = useState('working'); // working | done | failed
  const [message, setMessage] = useState('');

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const error = params.get('error');

    const finish = (ok, text) => {
      if (cancelled) return;
      setStatus(ok ? 'done' : 'failed');
      setMessage(text);
      if (window.opener && !window.opener.closed) {
        try {
          window.opener.postMessage(
            { type: LINKEDIN_MESSAGE, ok, error: ok ? null : text },
            window.location.origin,
          );
        } catch (e) {
          /* the opener went away; the user can close this window */
        }
        window.close();
      }
    };

    if (error || !code || !state) {
      finish(
        false,
        DECLINED.has(error)
          ? 'LinkedIn was not connected because the request was cancelled.'
          : 'LinkedIn did not complete the connection. Please try again.',
      );
      return undefined;
    }

    redeem(code, state)
      .then((res) => finish(true, res?.name ? `Connected as ${res.name}.` : 'LinkedIn connected.'))
      .catch((e) => finish(false, e.message || 'Could not connect LinkedIn. Please try again.'));

    return () => {
      cancelled = true;
    };
  }, []);

  const goBack = () => {
    let target = '/hirewithus/my-jobs';
    try {
      target = sessionStorage.getItem(LINKEDIN_RETURN_KEY) || target;
      sessionStorage.removeItem(LINKEDIN_RETURN_KEY);
    } catch (e) {
      /* storage unavailable */
    }
    window.location.replace(target);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50/50 px-4">
      <div className="w-full max-w-sm rounded-xl border border-border/60 bg-card p-8 text-center">
        {status === 'working' && (
          <>
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
            <p className="mt-4 text-sm font-medium text-foreground">Connecting your LinkedIn account…</p>
          </>
        )}
        {status === 'done' && (
          <>
            <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-600" />
            <p className="mt-4 text-sm font-medium text-foreground">{message}</p>
            <p className="mt-1 text-xs text-muted-foreground">You can close this window.</p>
            <Button className="mt-5" onClick={goBack}>
              Back to SearchJob.ai
            </Button>
          </>
        )}
        {status === 'failed' && (
          <>
            <AlertTriangle className="mx-auto h-8 w-8 text-amber-600" />
            <p className="mt-4 text-sm font-medium text-foreground">LinkedIn was not connected</p>
            <p className="mt-1 text-xs text-muted-foreground">{message}</p>
            <Button className="mt-5" variant="outline" onClick={goBack}>
              Back to SearchJob.ai
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
